import { ThrowError } from "@/api/callApi"
import type { TableColumn } from "@/components/common/CommonTable"
import CommonTable from "@/components/common/CommonTable"
import MainHeading from "@/components/common/MainHeading"
import { DepartmentService } from "@/services/Department/Department"
// import { RoutesLink } from "@/utility/RouteLinks"
import Layout from "@/components/layout/Layout"
import { Pencil, Plus, Trash, TriangleAlert, MoreHorizontal } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { department } from "@/interface/department.interface"
import { RoutesLink } from "@/utility/RouteLinks"
import { showToastNotification } from "@/utility/helper"
import { Button } from "@/components/ui/button"
import CommonModal from "@/components/common/CommonModal"
import CommonFilter from "@/components/common/CommonFilter"
import type { IFilter, IPageInfo } from "@/interface/common.interface"
import TablePagination from "@/components/common/TablePagination"
import { GetFilterUrl } from "@/utility/helper"

const Department: React.FC = () => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [department, setDepartment] = useState<department[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const [filter, setFilter] = useState<IFilter>({
        search: "",
    });

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };

    const handleSearchChange = (value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev;
            return { ...prev, search: value };
        });
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    };

    const handleGetDepartment = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        try {
            setIsLoading(true)
            const params = GetFilterUrl({ ...filter });
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${params ? `&${params}` : ""}`;
            const response = await DepartmentService?.getDepartment(PATH);
            if (response?.status) {
                setDepartment(response?.data?.department || [])
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }));
            }
            console.log("response", response)
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }


    const handleDeleteDepartment = async () => {
        try {
            const response = await DepartmentService?.deleteDepartment(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message)
                toggleDeleteDialog("")
                handleGetDepartment();
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
    }
    useEffect(() => {
        handleGetDepartment(paginationData.currentPage, paginationData.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter]);

    const toggleDeleteDialog = (id: string | undefined) => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen(!deleteDialogOpen);
    }
    const columns: TableColumn<department>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: department, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'departmentName',
            header: 'Department Name',
            render: (item: department) => (
                <span className="font-semibold capitalize">{item?.name}</span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: department) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isActive ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
                    {item?.isActive ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: department) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-48 rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            onClick={() => navigate(`${RoutesLink.editDepartment}/${item?.id}`)}
                            className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50"
                        >
                            <Pencil className="h-4 w-4" />Edit Department
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => toggleDeleteDialog(item?.id)}
                            className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50 text-red-600 focus:text-red-600"
                        >
                            <Trash className="h-4 w-4" />Delete Department
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ]

    return (
        <>
            <Layout>
                <div className='space-y-6'>
                    <MainHeading
                        title='Department'
                        buttonText='Add Department'
                        buttonIcon={<Plus />}
                        onClick={() => { navigate(RoutesLink.addDepartment) }}
                    />
                    <CommonFilter
                        searchValue={filter.search || ""}
                        onSearchChange={handleSearchChange}
                    />
                    <CommonTable data={department} columns={columns} loading={isLoading} />
                    <TablePagination
                        paginationData={paginationData}
                        setPaginationData={setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={isLoading}
                    />

                </div>
            </Layout>
            <CommonModal
                title=""
                show={deleteDialogOpen}
                onClose={() => toggleDeleteDialog("")}
                className="max-w-md rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <TriangleAlert className="text-red-600 w-8 h-8" />
                        </div>

                        <h2 className="mt-5 text-2xl font-bold text-gray-900">
                            Delete Department?
                        </h2>

                        <p className="mt-3 text-sm leading-6 text-gray-500">
                            This action cannot be undone. Once deleted, all department data will be
                            permanently removed from our servers.
                        </p>
                    </div>

                    <div className="mt-8 flex gap-3">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => toggleDeleteDialog("")}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={handleDeleteDepartment}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </CommonModal>
        </>
    )
}

export default Department